import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskLine, EASE } from "./Reveal";
import { openQuote } from "../lib/lenis";

const STATS = [
  ["15+", "Years in practice"],
  ["2200-hr", "Registered Massage Therapist"],
  ["6 yrs", "On world tours, backstage"],
  ["NHPC · FQM", "Registered & accredited"],
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const glowY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section id="top" ref={ref} className="relative overflow-hidden pt-28 pb-16 lg:pt-36 lg:pb-0" data-testid="hero-section">
      <motion.div style={{ y: glowY }} className="spotlight pointer-events-none absolute -top-40 right-[-15%] h-[42rem] w-[42rem] rounded-full" aria-hidden="true" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-ink" aria-hidden="true" />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-12 gap-14 lg:gap-8 items-center">
        <div className="lg:col-span-7">
          <MaskLine onLoad delay={0.1}>
            <p className="eyebrow flex items-center gap-3">
              <span className="inline-block h-px w-10 bg-gold/60" aria-hidden="true" />
              Elite Therapeutics — Therapeutic Bodywork
            </p>
          </MaskLine>
          <h1 className="mt-7 font-serif font-light tracking-[-0.02em] leading-[0.98] text-ivory text-[clamp(3rem,9vw,6.4rem)]">
            <MaskLine onLoad delay={0.22}>Backstage-tested.</MaskLine>
            <MaskLine onLoad delay={0.36}>
              <em className="italic text-gold font-light">Boardroom-ready.</em>
            </MaskLine>
          </h1>
          <MaskLine onLoad delay={0.5}>
            <p className="mt-7 max-w-xl text-mute text-base sm:text-lg leading-relaxed">
              Ariane Kamba — the wellness therapist trusted backstage on world tours with
              Post&nbsp;Malone, 21&nbsp;Savage and Live&nbsp;Nation — now brings fascia-first
              therapeutic bodywork to offices, events, productions and private clients
              across <span className="text-ivory">Alberta &amp; Montreal</span>.
            </p>
          </MaskLine>
          <motion.div
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.72, ease: EASE }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <button
              data-testid="hero-cta-corporate"
              onClick={() => openQuote("corporate")}
              className="group inline-flex items-center justify-center gap-3 rounded-full bg-gold px-8 py-4 font-mono text-[11px] uppercase tracking-[0.18em] font-medium text-ink transition-all duration-300 hover:bg-ivory hover:shadow-[0_0_44px_rgba(212,175,55,0.4)]"
            >
              Request a Corporate Wellness Quote
              <span className="transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true">→</span>
            </button>
            <button
              data-testid="hero-cta-private"
              onClick={() => openQuote("private")}
              className="inline-flex items-center justify-center gap-3 rounded-full border border-ivory/25 px-8 py-4 font-mono text-[11px] uppercase tracking-[0.18em] text-ivory transition-all duration-300 hover:border-gold hover:text-gold"
            >
              Book a Private Session
            </button>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.45, ease: EASE }}
          className="lg:col-span-5 relative"
        >
          <motion.div style={{ y: imgY }} className="relative">
            <div className="clip-notch relative overflow-hidden border border-white/10 bg-ink2" data-testid="hero-portrait">
              <img
                src="/images/t_fascia_table.jpg"
                alt="Ariane Kamba delivering hands-on therapeutic bodywork"
                className="h-[26rem] sm:h-[34rem] lg:h-[40rem] w-full object-cover object-bottom scale-[1.18] origin-bottom"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-transparent to-ink/20" aria-hidden="true" />
              <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between gap-4">
                <p className="font-serif italic text-2xl sm:text-3xl text-ivory leading-tight max-w-[16rem]">
                  “The body speaks… if you know how to listen.”
                </p>
              </div>
            </div>
            <div className="glass-chip absolute -top-4 -left-4 sm:-left-8 rounded-full px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-gold" data-testid="hero-chip-rmt">
              RMT · 2200 hrs
            </div>
            <div className="glass-chip absolute -bottom-4 right-6 rounded-full px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-ivory" data-testid="hero-chip-since">
              In practice since 2007
            </div>
          </motion.div>
        </motion.div>
      </div>

      <div className="relative mt-16 lg:mt-24 border-y border-white/10">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid grid-cols-2 lg:grid-cols-4">
          {STATS.map(([num, label], i) => (
            <div
              key={label}
              data-testid={`hero-stat-${i}`}
              className={`py-7 sm:py-9 px-4 sm:px-6 ${i % 2 === 1 ? "border-l border-white/10" : ""} ${i >= 2 ? "border-t lg:border-t-0 border-white/10" : ""} ${i > 0 ? "lg:border-l lg:border-white/10" : ""}`}
            >
              <p className="font-serif text-3xl sm:text-4xl text-gold">{num}</p>
              <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-mute">{label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
